import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { getCustomGPTs, createCustomGPT, updateCustomGPT, deleteCustomGPT, uploadKnowledgeBaseFile } from '../../../lib/api/customGPT'
import { ConfirmationDialog } from '../../shared/ConfirmationDialog'
import type { CustomGPT } from '../../../types/custom-gpt'
import { useAuth } from '../../auth/AuthProvider'
import { formatFileSize, isValidFileSize } from '../../../lib/fileUtils'

const ICONS: Record<string, JSX.Element> = {
  email: <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><rect x="2" y="4" width="14" height="10" rx="1.5" stroke="currentColor" strokeWidth="1.2"/><path d="M2 5.5l7 5 7-5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/></svg>,
  chart: <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M2 14l4-5 4 3 4-7" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/></svg>,
  doc:   <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><rect x="3" y="2" width="12" height="14" rx="1.5" stroke="currentColor" strokeWidth="1.2"/><path d="M6 7h6M6 10h4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/></svg>,
  bot:   <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><rect x="3" y="5" width="12" height="9" rx="2" stroke="currentColor" strokeWidth="1.2"/><circle cx="7" cy="9" r="1.2" fill="currentColor"/><circle cx="11" cy="9" r="1.2" fill="currentColor"/><path d="M7 3.5V5M11 3.5V5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/><path d="M5 14v1.5M13 14v1.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/></svg>,
}

function getIcon(name: string): JSX.Element {
  const l = name.toLowerCase()
  if (l.includes('email') || l.includes('mail')) return ICONS.email
  if (l.includes('report') || l.includes('sales') || l.includes('chart') || l.includes('perform')) return ICONS.chart
  if (l.includes('doc') || l.includes('summar') || l.includes('review') || l.includes('meet')) return ICONS.doc
  return ICONS.bot
}

function timeAgo(dateStr: string | null): string {
  if (!dateStr) return ''
  const diff = Date.now() - new Date(dateStr).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  const days = Math.floor(hrs / 24)
  if (days < 7) return `${days}d ago`
  return new Date(dateStr).toLocaleDateString()
}

function CreatePlaybookModal({ onClose, onCreated }: { onClose: () => void; onCreated: (g: CustomGPT) => void }) {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [instruction, setInstruction] = useState('')
  const [starters, setStarters] = useState([''])
  const [isPublic, setIsPublic] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [kbModalOpen, setKbModalOpen] = useState(false)
  const [kbFiles, setKbFiles] = useState<File[]>([])

  const handleCreate = async () => {
    if (!name.trim() || !instruction.trim()) { setError('Name and instructions are required.'); return }
    setSaving(true); setError('')
    try {
      const gpt = await createCustomGPT({ name: name.trim(), description: description.trim() || undefined, instruction: instruction.trim(), is_public: isPublic, conversation_starters: starters.filter(s => s.trim()) })
      if (kbFiles.length > 0) {
        const uploads = [...kbFiles]
        const ok: string[] = []
        const failed: string[] = []
        for (const f of uploads) {
          try {
            await uploadKnowledgeBaseFile(gpt.id, f)
            ok.push(f.name)
          } catch {
            failed.push(f.name)
          }
        }
        if (ok.length > 0) toast.success(`Uploaded ${ok.length} knowledge base file(s).`)
        if (failed.length > 0) toast.error(`Failed to upload: ${failed.join(', ')}`)
      }
      onCreated(gpt as unknown as CustomGPT)
      onClose()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to create playbook.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="modal-scrim" role="dialog" aria-modal="true" aria-labelledby="pbCreateTitle" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal create-modal">
        <div className="modal-head">
          <div><div className="modal-title" id="pbCreateTitle">Create a Playbook</div><div className="modal-sub">Build a custom AI workflow for your team</div></div>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close"><svg viewBox="0 0 14 14" fill="none"><path d="M2 2L12 12M12 2L2 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg></button>
        </div>
        <div className="modal-body">
          <div className="form-group"><div className="form-label">Name <span className="req">*</span></div><input className="form-input" type="text" placeholder="e.g. Client Email Writer" maxLength={100} value={name} onChange={e => setName(e.target.value)}/></div>
          <div className="form-group"><div className="form-label">Description</div><input className="form-input" type="text" placeholder="A brief description" maxLength={200} value={description} onChange={e => setDescription(e.target.value)}/></div>
          <div className="form-group"><div className="form-label">Instructions <span className="req">*</span></div><textarea className="form-input form-textarea" placeholder="You are a helpful assistant that specialises in…" maxLength={5000} value={instruction} onChange={e => setInstruction(e.target.value)}/></div>
          <div className="form-group">
            <div className="form-label">Knowledge Base</div>
            <div className="kb-row">
              <div className="kb-meta">
                <div className="kb-title">Add files for extra context</div>
                <div className="kb-sub">1 PDF up to 30MB</div>
              </div>
              <button type="button" className="kb-btn" onClick={() => setKbModalOpen(true)}>
                {kbFiles.length > 0 ? `Manage (${kbFiles.length})` : 'Add files'}
              </button>
            </div>
          </div>
          <div className="form-group">
            <div className="form-label">Conversation Starters</div>
            <div className="starters-list">
              {starters.map((s, i) => (
                <div key={i} className="starter-item">
                  <input className="form-input" type="text" placeholder={`Conversation starter ${i + 1}`} value={s} onChange={e => setStarters(prev => prev.map((x, j) => j === i ? e.target.value : x))}/>
                  <button type="button" className="starter-remove" onClick={() => setStarters(prev => prev.filter((_, j) => j !== i))} aria-label="Remove starter">
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2 2l8 8M10 2L2 10" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/></svg>
                  </button>
                </div>
              ))}
            </div>
            <button type="button" className="add-starter" onClick={() => setStarters(p => [...p, ''])}>
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M6 1v10M1 6h10" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/></svg>
              Add starter
            </button>
          </div>
          <div className="form-group">
            <div className="form-label">Visibility</div>
            <div className="visibility-row">
              <div className="vis-info"><h4>Make this playbook public</h4><p>Other team members can see and use it</p></div>
              <button type="button" className={`vis-tog${!isPublic ? ' off' : ''}`} onClick={() => setIsPublic(p => !p)} role="switch" aria-checked={isPublic} aria-label="Toggle visibility"><div className="vis-knob"/></button>
            </div>
          </div>
          {error && <div className="form-error">{error}</div>}
          <div className="form-actions">
            <button type="button" className="btn-cancel" onClick={onClose}>Cancel</button>
            <button type="button" className="btn-create" onClick={handleCreate} disabled={saving}>{saving ? 'Creating…' : 'Create Playbook'}</button>
          </div>
        </div>
      </div>

      {kbModalOpen && (
        <div className="modal-scrim" role="dialog" aria-modal="true" aria-label="Knowledge base files" onClick={e => e.target === e.currentTarget && setKbModalOpen(false)}>
          <div className="modal kb-modal">
            <div className="modal-head">
              <div>
                <div className="modal-title">Knowledge base</div>
                <div className="modal-sub">Upload files so this playbook can reference them</div>
              </div>
              <button type="button" className="modal-close" onClick={() => setKbModalOpen(false)} aria-label="Close">
                <svg viewBox="0 0 14 14" fill="none"><path d="M2 2L12 12M12 2L2 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
              </button>
            </div>
            <div className="modal-body">
              <div className="kb-drop">
                <input
                  id="kbFileInput"
                  type="file"
                  accept=".pdf,application/pdf"
                  className="kb-file-input"
                  onChange={(e) => {
                    const files = Array.from(e.target.files ?? [])
                    if (!files.length) return
                    if (files.length > 1) {
                      toast.error('Only 1 PDF can be added.')
                      e.currentTarget.value = ''
                      return
                    }
                    const f = files[0]!
                    const name = f.name.toLowerCase()
                    const isPdf = name.endsWith('.pdf') || f.type === 'application/pdf'
                    if (!isPdf) {
                      toast.error(`${f.name}: only PDF files are allowed`)
                      e.currentTarget.value = ''
                      return
                    }
                    if (!isValidFileSize(f)) {
                      toast.error(`${f.name}: too large (max 30MB)`)
                      e.currentTarget.value = ''
                      return
                    }
                    // Only 1 file total: replace any existing selection.
                    setKbFiles([f])
                    e.currentTarget.value = ''
                  }}
                />
                <label htmlFor="kbFileInput" className="kb-drop-label">
                  Click to add PDF
                </label>
              </div>

              {kbFiles.length > 0 ? (
                <div className="kb-list">
                  {kbFiles.map((f, idx) => (
                    <div key={`${f.name}-${idx}`} className="kb-item">
                      <div className="kb-item-left">
                        <div className="kb-fn">{f.name}</div>
                        <div className="kb-fm">{formatFileSize(f.size)}</div>
                      </div>
                      <button type="button" className="kb-remove" onClick={() => setKbFiles(prev => prev.filter((_, i) => i !== idx))} aria-label={`Remove ${f.name}`}>
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2 2l8 8M10 2L2 10" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/></svg>
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="kb-empty">No knowledge base files added yet.</div>
              )}

              <div className="form-actions">
                <button type="button" className="btn-cancel" onClick={() => { setKbFiles([]); setKbModalOpen(false) }}>Clear</button>
                <button type="button" className="btn-create" onClick={() => setKbModalOpen(false)}>Done</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function EditPlaybookModal({ playbook, onClose, onSaved }: { playbook: CustomGPT; onClose: () => void; onSaved: (g: CustomGPT) => void }) {
  const [name, setName] = useState(playbook.name)
  const [description, setDescription] = useState(playbook.description ?? '')
  const [instruction, setInstruction] = useState(playbook.instruction)
  const [starters, setStarters] = useState<string[]>(playbook.conversation_starters?.length ? playbook.conversation_starters : [''])
  const [isPublic, setIsPublic] = useState(playbook.is_public === 'true')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const handleSave = async () => {
    if (!name.trim() || !instruction.trim()) { setError('Name and instructions are required.'); return }
    setSaving(true); setError('')
    try {
      const updated = await updateCustomGPT(playbook.id, {
        name: name.trim(),
        description: description.trim() || undefined,
        instruction: instruction.trim(),
        is_public: isPublic,
        conversation_starters: starters.filter(s => s.trim()),
      })
      onSaved({ ...playbook, ...updated } as unknown as CustomGPT)
      onClose()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save playbook.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="modal-scrim" role="dialog" aria-modal="true" aria-labelledby="pbEditTitle" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal create-modal">
        <div className="modal-head">
          <div><div className="modal-title" id="pbEditTitle">Edit Playbook</div><div className="modal-sub">Update your custom AI workflow</div></div>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close"><svg viewBox="0 0 14 14" fill="none"><path d="M2 2L12 12M12 2L2 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg></button>
        </div>
        <div className="modal-body">
          <div className="form-group"><div className="form-label">Name <span className="req">*</span></div><input className="form-input" type="text" placeholder="e.g. Client Email Writer" maxLength={100} value={name} onChange={e => setName(e.target.value)}/></div>
          <div className="form-group"><div className="form-label">Description</div><input className="form-input" type="text" placeholder="A brief description" maxLength={200} value={description} onChange={e => setDescription(e.target.value)}/></div>
          <div className="form-group"><div className="form-label">Instructions <span className="req">*</span></div><textarea className="form-input form-textarea" placeholder="You are a helpful assistant that specialises in…" maxLength={5000} value={instruction} onChange={e => setInstruction(e.target.value)}/></div>
          <div className="form-group">
            <div className="form-label">Conversation Starters</div>
            <div className="starters-list">
              {starters.map((s, i) => (
                <div key={i} className="starter-item">
                  <input className="form-input" type="text" placeholder={`Conversation starter ${i + 1}`} value={s} onChange={e => setStarters(prev => prev.map((x, j) => j === i ? e.target.value : x))}/>
                  <button type="button" className="starter-remove" onClick={() => setStarters(prev => prev.filter((_, j) => j !== i))} aria-label="Remove starter">
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2 2l8 8M10 2L2 10" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/></svg>
                  </button>
                </div>
              ))}
            </div>
            <button type="button" className="add-starter" onClick={() => setStarters(p => [...p, ''])}>
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M6 1v10M1 6h10" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/></svg>
              Add starter
            </button>
          </div>
          <div className="form-group">
            <div className="form-label">Visibility</div>
            <div className="visibility-row">
              <div className="vis-info"><h4>Make this playbook public</h4><p>Other team members can see and use it</p></div>
              <button type="button" className={`vis-tog${!isPublic ? ' off' : ''}`} onClick={() => setIsPublic(p => !p)} role="switch" aria-checked={isPublic} aria-label="Toggle visibility"><div className="vis-knob"/></button>
            </div>
          </div>
          {error && <div className="form-error">{error}</div>}
          <div className="form-actions">
            <button type="button" className="btn-cancel" onClick={onClose}>Cancel</button>
            <button type="button" className="btn-create" onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save Changes'}</button>
          </div>
        </div>
      </div>
    </div>
  )
}

export function Playbooks() {
  const navigate = useNavigate()
  const { dbUser } = useAuth()
  const [tab, setTab] = useState<'team' | 'mine'>('team')
  const [gpts, setGpts] = useState<CustomGPT[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [createOpen, setCreateOpen] = useState(false)
  const [editingPlaybook, setEditingPlaybook] = useState<CustomGPT | null>(null)
  const [playbookToDelete, setPlaybookToDelete] = useState<CustomGPT | null>(null)

  useEffect(() => {
    setLoading(true)
    getCustomGPTs(1, 100)
      .then(res => { setGpts(res.items); setError(null) })
      .catch(e => setError(e instanceof Error ? e.message : 'Failed to load playbooks'))
      .finally(() => setLoading(false))
  }, [])

  const teamData = gpts.filter(g => g.is_public === 'true' && g.is_deleted !== 'true')
  const mineData = gpts.filter(g => g.is_deleted !== 'true' && g.user_id === dbUser?.id)
  const data = tab === 'team' ? teamData : mineData

  return (
    <div className="playbooks-scope">
      <div className="section-view">
        <div className="sv-header">
          <div>
            <div className="sv-title">Playbooks</div>
            <div className="sv-subtitle">Pre-built and custom AI workflows for your team</div>
          </div>
          <button type="button" className="create-btn" onClick={() => setCreateOpen(true)}>
            <svg viewBox="0 0 14 14" fill="none"><path d="M7 1v12M1 7h12" stroke="white" strokeWidth="1.5" strokeLinecap="round"/></svg>
            Create Playbook
          </button>
        </div>

        <div className="pb-tabs">
          <button type="button" className={`pb-tab ${tab === 'team' ? 'active' : ''}`.trim()} onClick={() => setTab('team')}>Team Playbooks</button>
          <button type="button" className={`pb-tab ${tab === 'mine' ? 'active' : ''}`.trim()} onClick={() => setTab('mine')}>My Playbooks</button>
        </div>

        {loading ? (
          <div className="pb-loading">Loading playbooks…</div>
        ) : error ? (
          <div className="pb-error">{error}</div>
        ) : (
          <div className="pb-grid">
            {data.length === 0 ? (
              <div className="pb-empty">
                {tab === 'team' ? 'No public playbooks yet.' : 'No playbooks yet. Create your first one.'}
              </div>
            ) : data.map(p => (
              <button
                key={p.id}
                type="button"
                className="pb-card"
                onClick={() => navigate(`/playbooks/${p.id}/chat`)}
                aria-label={`Open ${p.name}`}
              >
                <div className="pb-card-head">
                  <div className="pb-card-icon" aria-hidden="true">{getIcon(p.name)}</div>
                  <div className="pb-card-head-right">
                    <div className="pb-tag">{p.is_public === 'true' ? 'TEAM' : 'MINE'}</div>
                  </div>
                  {tab === 'mine' && (
                    <div className="pb-card-actions">
                      <button
                        type="button"
                        className="pb-edit-btn"
                        onClick={e => { e.stopPropagation(); setEditingPlaybook(p) }}
                        aria-label={`Edit ${p.name}`}
                        title="Edit"
                      >
                        <svg width="13" height="13" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                          <path d="M8.9 2.1l3 3M2 12h3l7.6-7.6a1 1 0 000-1.4l-1.6-1.6a1 1 0 00-1.4 0L2 9v3z" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </button>
                      <button
                        type="button"
                        className="pb-delete-btn"
                        onClick={e => { e.stopPropagation(); setPlaybookToDelete(p) }}
                        aria-label={`Delete ${p.name}`}
                        title="Delete"
                      >
                        <svg width="13" height="13" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                          <path d="M3.5 3.5h7M5.5 3.5V2.5a1 1 0 011-1h1a1 1 0 011 1v1M11.5 3.5v8a1.5 1.5 0 01-1.5 1.5h-6A1.5 1.5 0 012.5 11.5v-8h9zM5.5 6.5v4.5M8.5 6.5v4.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </button>
                    </div>
                  )}
                </div>
                <div className="pb-card-name">{p.name}</div>
                <div className="pb-card-desc">{p.description?.trim() || '\u00A0'}</div>
                <div className="pb-card-meta">
                  {p.updated_at ? `Updated ${timeAgo(p.updated_at)}` : '\u00A0'}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {createOpen && (
        <CreatePlaybookModal
          onClose={() => setCreateOpen(false)}
          onCreated={g => setGpts(prev => [g, ...prev])}
        />
      )}
      {editingPlaybook && (
        <EditPlaybookModal
          playbook={editingPlaybook}
          onClose={() => setEditingPlaybook(null)}
          onSaved={updated => setGpts(prev => prev.map(g => g.id === updated.id ? updated : g))}
        />
      )}
      <ConfirmationDialog
        open={!!playbookToDelete}
        onOpenChange={open => { if (!open) setPlaybookToDelete(null) }}
        title="Delete playbook?"
        description={
          playbookToDelete
            ? `“${playbookToDelete.name}” will be removed for you. This cannot be undone.`
            : ''
        }
        confirmText="Delete"
        variant="destructive"
        onConfirm={() => {
          const id = playbookToDelete?.id
          if (!id) return
          void (async () => {
            try {
              await deleteCustomGPT(id)
              setGpts(prev => prev.filter(g => g.id !== id))
              toast.success('Playbook deleted.')
            } catch (e) {
              toast.error(e instanceof Error ? e.message : 'Failed to delete playbook.')
            }
          })()
        }}
      />
    </div>
  )
}
